import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  MechanicalInspection,
  ReturnVehicleCheckListValue,
  Select,
} from '@/types';
import { getFormattedLabel } from '@/utils/helpers';

import { mechanicalInspection } from '../mechanicalInspection';

export const useMechanicalInspectionAvailable = (
  id: number,
  barCode: string | number,
  options?: {
    enabled?: boolean;
  },
) =>
  useQuery({
    queryKey: [QUERY_KEYS.mechanicalInspectionAvailable, id, barCode],
    queryFn: async () =>
      (await mechanicalInspection.getMechanicalInspectionAvailable(id, barCode))
        .data,
    enabled: options?.enabled,
  });

export const getMechanicalInspectionItemsByValue = (
  values: MechanicalInspection | undefined,
  value: ReturnVehicleCheckListValue,
) =>
  values
    ? Object.entries(values)?.reduce(
        (acc, [category, categoryItems]) => {
          const res = [...acc];

          Object.entries(categoryItems || {}).forEach(
            ([itemKey, itemValue]) => {
              if (itemValue === value) {
                res.push({
                  category,
                  item: itemKey,
                });
              }
            },
          );

          return res;
        },
        [] as Array<{ category: string; item: string }>,
      )
    : [];

export const useMechanicalInspection = (
  id: number,
  barCode: string | number,
  options?: {
    enabled?: boolean;
  },
) =>
  useQuery({
    queryKey: [QUERY_KEYS.mechanicalInspection, id, barCode],
    queryFn: async () => {
      const { data } = await mechanicalInspection.getMechanicalInspection(
        id,
        barCode,
      );

      const failToReportItems = getMechanicalInspectionItemsByValue(
        data,
        ReturnVehicleCheckListValue.FailToReport,
      );

      const failToReportList: Select[] = failToReportItems?.map((failItem) => {
        const { item, category } = failItem;
        const value = `${category}.${item}`;
        const label = getFormattedLabel(item);

        return {
          label,
          value,
        };
      });

      return {
        mechanicalList: data,
        isMechanical: !!failToReportItems.length,
        failToReportItems,
        failToReportList,
      };
    },
    enabled: options?.enabled,
  });
